import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, AlertTriangle } from 'lucide-react';
import { useMyIssues } from '../../hooks/useMyHome';
import type { IssueItem } from '../../hooks/useMyHome';
import Spinner from '../ui/Spinner';
import IssueDetailPanel from '../issue/IssueDetailPanel';
import { useProject } from '../../hooks/useProjects';

type FilterTab = 'assigned' | 'reported';

interface StatusColumnDef {
  key: 'todo' | 'inProgress' | 'inReview' | 'done' | 'overdue';
  label: string;
  headerColor: string;
  countColor: string;
  isOverdue?: boolean;
}

const COLUMNS: StatusColumnDef[] = [
  { key: 'todo', label: '할 일', headerColor: 'text-gray-700', countColor: 'text-gray-900' },
  { key: 'inProgress', label: '진행 중', headerColor: 'text-blue-700', countColor: 'text-blue-900' },
  { key: 'inReview', label: '검토 중', headerColor: 'text-purple-700', countColor: 'text-purple-900' },
  { key: 'done', label: '완료', headerColor: 'text-green-700', countColor: 'text-green-900' },
  { key: 'overdue', label: '기한 초과', headerColor: 'text-red-700', countColor: 'text-red-900', isOverdue: true },
];

interface SelectedIssue {
  issueId: string;
  projectId: string;
  projectKey: string;
}

function IssueRow({
  issue,
  isOverdue,
  onClick,
}: {
  issue: IssueItem;
  isOverdue?: boolean;
  onClick: (issue: IssueItem) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onClick(issue)}
      className="w-full text-left block px-2 py-1.5 rounded hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-start gap-1.5">
        {isOverdue && <AlertTriangle className="size-3 text-red-500 mt-0.5 shrink-0" />}
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-primary-600 shrink-0">{issue.key}</span>
            <span className="text-sm text-gray-700 truncate">{issue.title}</span>
          </div>
          <span className="text-[11px] text-gray-400">{issue.projectName}</span>
        </div>
      </div>
    </button>
  );
}

function StatusColumn({
  col,
  count,
  issues,
  onIssueClick,
}: {
  col: StatusColumnDef;
  count: number;
  issues: IssueItem[];
  onIssueClick: (issue: IssueItem) => void;
}) {
  const borderClass = col.isOverdue ? 'border-red-200 bg-red-50/50' : 'border-gray-200 bg-white';

  return (
    <div className={`flex-1 min-w-[160px] border rounded-lg ${borderClass}`}>
      {/* 헤더 */}
      <div className="px-3 py-2.5 border-b border-inherit">
        <p className={`text-sm font-medium ${col.headerColor}`}>{col.label}</p>
        <p className={`text-xl font-bold ${col.countColor}`}>{count}</p>
      </div>
      {/* 이슈 목록 */}
      <div className="px-1 py-1.5 space-y-0.5">
        {issues.length > 0 ? (
          issues.map((issue) => (
            <IssueRow key={issue.id} issue={issue} isOverdue={col.isOverdue} onClick={onIssueClick} />
          ))
        ) : (
          <p className="text-[11px] text-gray-300 text-center py-3">없음</p>
        )}
        {count > 5 && (
          <Link
            to="/filters"
            className="flex items-center justify-center gap-0.5 py-1 text-[11px] text-primary-500 hover:text-primary-700"
          >
            +{count - 5}건 더보기 <ArrowRight className="size-2.5" />
          </Link>
        )}
      </div>
    </div>
  );
}

// 사이드 패널 렌더링용 wrapper — 프로젝트 정보를 동적 로드 (마이 홈 페이지에는 PanelContext가 없음)
function MyHomeIssueDetail({
  selected,
  onClose,
}: {
  selected: SelectedIssue;
  onClose: () => void;
}) {
  const { data: project } = useProject(selected.projectId);
  // 백드롭
  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-30" onClick={onClose} />
      <IssueDetailPanel
        projectId={selected.projectId}
        projectKey={selected.projectKey}
        issueId={selected.issueId}
        onClose={onClose}
        members={project?.members}
        workflowStatuses={project?.workflow?.statuses}
        projectWorkflow={project?.workflow}
      />
    </>
  );
}

export default function MyIssuesDashboard() {
  const [filter, setFilter] = useState<FilterTab>('assigned');
  const [selected, setSelected] = useState<SelectedIssue | null>(null);
  const { data, isLoading } = useMyIssues(filter);

  const handleIssueClick = (issue: IssueItem) => {
    setSelected({ issueId: issue.id, projectId: issue.projectId, projectKey: issue.projectKey });
  };

  return (
    <div>
      {/* 탭 */}
      <div className="flex bg-gray-100 rounded-lg p-0.5 mb-4 w-fit">
        {([
          { id: 'assigned' as const, label: '나에게 할당됨' },
          { id: 'reported' as const, label: '내가 보고함' },
        ]).map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setFilter(tab.id)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              filter === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <Spinner />
      ) : !data ? (
        <p className="text-sm text-gray-400">데이터를 불러올 수 없습니다.</p>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {COLUMNS.map((col) => (
            <StatusColumn
              key={col.key}
              col={col}
              count={data.summary[col.key]}
              issues={data.issues[col.key]}
              onIssueClick={handleIssueClick}
            />
          ))}
        </div>
      )}

      {/* 사이드 패널 (마이 홈에서 자체 렌더) */}
      {selected && (
        <MyHomeIssueDetail selected={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
